import Vue from 'vue'
import { Component, Watch } from 'vue-property-decorator'
import * as types from '@/store/types'
import { mapGetters, mapMutations } from 'vuex'

import SearchBox from '../components/SearchBox.vue'
import RecipientList from '../components/RecipientList.vue'
import PageNavigationBar from '../components/PageNavigationBar.vue'
import ModalLayout from '../components/ModalLayout.vue'

import api from '@/modules/sentstatus/api'

@Component({
  components: {
    SearchBox,
    RecipientList,
    PageNavigationBar,
    ModalLayout,
  },
  computed: mapGetters({
    currentItem: types.CURRENT_MAIL_ITEM,
    validDomain: types.VALID_DOMAIN,
    originalList: types.ORIGINAL_RECIPIENTS_GET,
    indexedList: types.INDEXED_RECIPIENTS_GET,
    recipientWithStatusList: types.RECIPIENTS_WITH_STATUS_GET,
    searchTarget: types.SEARCH_TARGET_GET,
  }),
  methods: mapMutations({
    setOriginalList: types.ORIGINAL_RECIPIENTS_SET,
    setIndexedList: types.INDEXED_RECIPIENTS_SET,
    setRecipientWithStatus: types.RECIPIENTS_WITH_STATUS_SET,
    setSearchTarget: types.SEARCH_TARGET_SET,
  }),
})
export default class MainView extends Vue {
  //
  // ──────────────────────────────────────────────────────── I ──────────
  //   :::::: G E T T E R S : :  :   :    :     :        :          :
  // ──────────────────────────────────────────────────────────────────
  //
  currentItem!: Office.MessageRead;
  validDomain!: string[];
  originalList!: Recipient[];
  indexedList!: Recipient[];
  recipientWithStatusList!: RecipientWithStatus[];
  searchTarget!: Recipient[];

  //
  // ────────────────────────────────────────────────────────── I ──────────
  //   :::::: M U T A T I O N S : :  :   :    :     :        :          :
  // ────────────────────────────────────────────────────────────────────
  //
  setOriginalList!: (payload: Recipient[]) => void;
  setIndexedList!: (payload: Recipient[]) => void;
  setRecipientWithStatus!: (payload: RecipientWithStatus[]) => void;
  setSearchTarget!: (paylaod: Recipient[]) => void;

  //
  // ──────────────────────────────────────────────────────── I ──────────
  //   :::::: D A T A : :  :   :    :     :        :          :
  // ───────────────────────────────────────────s───────────────────────
  //
  currentMetaData: recall.ItemMetaData = Object.create(null);
  recallStatusList: recall.RecallStatus[] = [];

  isFromMe: boolean = false;
  isLoading: boolean = true;
  isBtnDisabled: boolean = false;
  isModalOpen: boolean = false;

  keyword: string = '';

  pageIndex: number = 1;
  pageSize: number = 8;
  pageRange: number = 5;

  recallMessage: string = '';

  //
  // ──────────────────────────────────────────────────────── I ──────────
  //   :::::: C O M P U T E D : :  :   :    :     :        :          :
  // ──────────────────────────────────────────────────────────────────
  //
  get selectCount() {
    return this.recipientWithStatusList.filter((x) => x.isSelected).length;
  }

  get totalListLength() {
    if (this.keyword) {
      return this.searchTarget.length;
    }

    return this.originalList.length;
  }

  //
  // ────────────────────────────────────────────────────── I ──────────
  //   :::::: M E T H O D S : :  :   :    :     :        :          :
  // ────────────────────────────────────────────────────────────────
  //
  isExternal(emailAddress: string) {
    const domain = emailAddress.toLowerCase().split('@')[1];
    const isValidDomain = this.validDomain.findIndex(x => x == domain) > -1;

    return !isValidDomain;
  }

  async setMetaData() {
    let encodedItemId = encodeURIComponent(this.currentItem.itemId);

    const metaData = await api.recall.getItemMetadata(encodedItemId);
    this.currentMetaData = metaData;
  }

  async setOriginalRecipientList() {
    const toList = this.currentItem.to;
    const ccList = this.currentItem.cc;

    const recipientList = toList.concat(ccList);

    let userList = recipientList.filter(x => x.recipientType !== 'distributionList') as Recipient[];
    const distributionList = recipientList.filter(x => x.recipientType == 'distributionList') as Recipient[];

    for (const distribution of distributionList) {
      const data = await api.group.expandGroup(distribution.emailAddress);
      data.members.forEach(x => {
        const duplicateUser = userList.find(y => y.emailAddress == x.address);
        if (!duplicateUser) {
          userList.push({
            displayName: x.name,
            emailAddress: x.address,
            recipientType: 'user',
          });
        }
      });
    }
    this.setOriginalList(userList);
  }

  setIndexedRecipientList() {
    let originalList = [...this.originalList];

    if (this.keyword) {
      originalList = [...this.searchTarget];
    }

    const indexedList = originalList.slice((this.pageIndex - 1) * this.pageSize, this.pageIndex * this.pageSize);

    this.setIndexedList(indexedList);
  }

  setRecipientWithStatusList(sentStatusList: sentstatus.SentStatus[]) {
    let result: RecipientWithStatus[] = [];
    let indexedList = this.indexedList;

    for (let i = 0; i < indexedList.length; i++) {
      let targetRecipient: RecipientWithStatus = Object.create(null);

      let currentRecipient = indexedList[i];
      let currentRecallStatus = this.recallStatusList.find(x => x.emailAddress == currentRecipient.emailAddress);

      let currentSentStatus = sentStatusList.find(x => x.recipient === currentRecipient.emailAddress);

      if (currentSentStatus) {
        targetRecipient = { ...currentSentStatus };
      }

      // 회수요청이 된 메일의 경우, sentStatus만 가지고 있는 수신자 목록에 recallStatus 추가
      if (currentRecallStatus) {
        targetRecipient.recallStatus = currentRecallStatus.recallStatus;
      }

      targetRecipient.displayName = currentRecipient.displayName;
      targetRecipient.recipient = currentRecipient.emailAddress;
      targetRecipient.recipientType = currentRecipient.recipientType;
      targetRecipient.isSelected = false;

      result.push(targetRecipient);
    }

    this.setRecipientWithStatus(result);
  }

  @Watch('pageIndex')
  async loadData() {
    this.isLoading = true;
    this.isBtnDisabled = true;

    await this.setOriginalRecipientList();

    let encodedSearchKey = encodeURIComponent(this.currentMetaData.searchKey);

    this.setIndexedRecipientList();

    let indexedList = this.indexedList;
    let internalRecipientList = indexedList.filter(x => !this.isExternal(x.emailAddress));

    const sentStatusRequest: sentstatus.SentStatusRequest = {
      itemId: this.currentItem.itemId,
      searchKey: this.currentMetaData.searchKey,
      recipients: internalRecipientList,
    };

    // 회수 요청이 있으면 RecallStatus 조회해서 회수 요청된 수신자 목록 생성
    if (this.currentMetaData.recallStatus) {
      const recallStatus = await api.recall.getRecallStatus(encodedSearchKey);
      let recallReqList = recallStatus.requests;

      recallReqList.forEach(recallReq => {
        this.recallStatusList.push(...recallReq.recallStatuses);
      });
    }

    // 메일 SentStatus 조회해서 필요한 데이터가 들어 있는 수신자 목록 생성
    const sentStatusReq = await api.sentstatus.sentStatus(sentStatusRequest);
    this.setRecipientWithStatusList(sentStatusReq.sentStatuses);

    this.isLoading = false;
    this.isBtnDisabled = false;
  }

  handleSearch(value: string) {
    this.keyword = value.toLowerCase();

    let originalList = [...this.originalList];
    originalList = originalList.filter(
      x => x.displayName.toLowerCase().includes(this.keyword) || x.emailAddress.toLowerCase().includes(this.keyword),
    );

    this.setSearchTarget(originalList);

    // 검색 결과가 1 페이지부터 표시되도록 pageIndex 리셋
    if ((this.pageIndex = 1)) {
      this.loadData();
    } else {
      this.pageIndex = 1;
    }
  }

  async recall(internalRecipientList: Recipient[]) {
    this.isBtnDisabled = true;

    let recallReq: recall.RecallRequest = Object.create(null);

    recallReq.itemId = this.currentItem.itemId;
    recallReq.searchKey = this.currentMetaData.searchKey;
    recallReq.recipients = internalRecipientList;

    const recallResult = await api.recall.requestMainRecall(recallReq);

    if(recallResult.success) this.currentMetaData.recallStatus = "Success";

    this.isBtnDisabled = false;

    this.isModalOpen = true;
    this.recallMessage = recallResult.success ? '회수요청_성공' : '회수요청_실패';

    this.loadData();
  }

  handleRecall() {
    if (this.selectCount == 0) {
      this.isModalOpen = true;
      this.recallMessage = '회수대상_선택바람';
      return;
    }

    // 외부메일 및 이미 요청된 메일 제외
    let internalRecipientList = this.recipientWithStatusList.filter(
      x => !this.isExternal(x.recipient) && !x.recallStatus && x.isSelected,
    );

    // 메일없음 제외
    internalRecipientList = internalRecipientList.filter(x => x.mailStatus !== 'NotFound');


    if (internalRecipientList.length == 0) {
      this.isModalOpen = true;
      this.recallMessage = '회수가능메일_없음';
      return;
    }

    let convertedList: Recipient[] = internalRecipientList.map(x => ({
      displayName: x.displayName!,
      emailAddress: x.recipient,
      recipientType: x.recipientType!,
    }));

    this.recall(convertedList);
  }

  handleAllRecall() {
    let originalList = [...this.originalList];

    originalList.forEach(x => {
      let isRecall = this.recallStatusList.findIndex(y => x.emailAddress == y.emailAddress) > -1;

      if (isRecall) {
        originalList = originalList.filter(z => z.emailAddress !== x.emailAddress);
      }
    });

    // 외부메일 제외
    let internalRecipientList = originalList.filter(x => !this.isExternal(x.emailAddress));

    if (internalRecipientList.length == 0) {
      this.isModalOpen = true;
      this.recallMessage = '회수가능메일_없음';
      return;
    }

    this.recall(internalRecipientList);
  }

  pageMove(pageIndex: number) {
    this.pageIndex = pageIndex;
  }

  //
  // ──────────────────────────────────────────────────────── I ──────────
  //   :::::: L I F E C Y L E : :  :   :    :     :        :          :
  // ──────────────────────────────────────────────────────────────────
  //
  created() {
    this.isFromMe = Office.context.mailbox.userProfile.emailAddress == this.currentItem.sender.emailAddress;
  }

  async mounted() {
    if (this.isFromMe) {
      await this.setMetaData();
      this.loadData();
    }
  }
}